<template>
  <div>
    <v-container>
      <v-alert v-if="forbidden" type="error" class="ma-4">
        คุณไม่มีสิทธิ์เข้าถึงหน้านี้ (เฉพาะ SuperAdmin เท่านั้น)
      </v-alert>

      <template v-else>
        <base-material-card
          icon="mdi-shield-alert"
          title="ตรวจสอบความปลอดภัยระบบ (SuperAdmin)"
          class="px-5 py-3"
        >
          <v-row class="mb-4">
            <v-col cols="12" sm="6" md="3">
              <v-card color="error" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ summary.locked_accounts_now }}</div>
                  <div>บัญชีถูกล็อกตอนนี้</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card color="warning" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ summary.failed_logins_24h }}</div>
                  <div>Login ล้มเหลว (24 ชม.)</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card color="deep-orange" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ summary.access_denied_24h }}</div>
                  <div>ถูกปฏิเสธสิทธิ์ (24 ชม.)</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card color="success" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ summary.active_sessions_now }}</div>
                  <div>Session ที่ active อยู่</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col cols="12" sm="6" md="3">
              <v-card color="purple" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ summary.sensitive_export_24h }}</div>
                  <div>ส่งออกข้อมูลลับ (24 ชม.)</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card color="deep-orange" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ summary.impersonations_24h }}</div>
                  <div>สวมสิทธิ์ผู้ใช้งาน (24 ชม.)</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col cols="12" md="4">
              <v-card color="indigo" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ summary.password_change_pending.total }}</div>
                  <div>บัญชียังไม่เปลี่ยนรหัสผ่าน (แคมเปญ)</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="8">
              <v-card outlined>
                <v-card-text>
                  <div class="d-flex justify-space-around text-center">
                    <div>
                      <div class="text-h6">{{ summary.password_change_pending.user }}</div>
                      <div class="caption">ผู้ใช้งานระบบ (user)</div>
                    </div>
                    <div>
                      <div class="text-h6">{{ summary.password_change_pending.personnel_temporary }}</div>
                      <div class="caption">ข้าราชการครู (personnel_temporary)</div>
                    </div>
                    <div>
                      <div class="text-h6">{{ summary.password_change_pending.personnel_etc }}</div>
                      <div class="caption">พนักงานราชการ/ลูกจ้าง (personnel_etc)</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-btn color="primary" small class="mb-4" @click="loadAll">
            <v-icon left small>mdi-refresh</v-icon>รีเฟรชข้อมูล
          </v-btn>

          <!-- Locked Accounts -->
          <v-card class="mb-8" outlined>
            <v-card-title class="text-h6 error--text">
              <v-icon left color="error">mdi-lock-alert</v-icon>
              บัญชีที่ถูกล็อกอยู่ตอนนี้
            </v-card-title>
            <v-card-subtitle>
              ล็อกอัตโนมัติเมื่อกรอกรหัสผ่านผิดครบ 5 ครั้งภายใน 15 นาที — ปกติจะปลดล็อกเองเมื่อครบเวลา
              แต่สามารถกดยกเลิกการล็อกก่อนกำหนดได้ที่นี่
            </v-card-subtitle>
            <v-card-text>
              <v-alert v-if="!lockedAccounts.length" type="success" text class="mb-0">
                ไม่มีบัญชีที่ถูกล็อกอยู่ตอนนี้
              </v-alert>
              <v-data-table
                v-else
                :headers="lockedAccountHeaders"
                :items="lockedAccounts"
                :loading="loadingLockedAccounts"
                dense
              >
                <template v-slot:item.display_name="{ item }">
                  {{ item.display_name || "-" }}
                </template>
                <template v-slot:item.failed_count="{ item }">
                  <v-chip x-small color="error" dark>{{ item.failed_count }}</v-chip>
                </template>
                <template v-slot:item.retry_after="{ item }">
                  {{ Math.ceil(item.retry_after / 60) }} นาที
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn x-small color="success" dark @click="unlockAccount(item)">
                    <v-icon left x-small>mdi-lock-open-variant</v-icon>ยกเลิกการล็อก
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Suspicious Users -->
          <v-card class="mb-8" outlined>
            <v-card-title class="text-h6 amber--text text--darken-3">
              <v-icon left color="amber darken-3">mdi-account-alert</v-icon>
              พฤติกรรมผิดปกติของผู้ใช้งาน
            </v-card-title>
            <v-card-subtitle>
              วิเคราะห์จากข้อมูลย้อนหลัง {{ suspiciousParams.days }} วัน — รวม 4 สัญญาณ:
              พยายามเข้าถึงหน้าที่ไม่มีสิทธิ์, brute force, เข้าใช้งานนอกเวลา, และอัปโหลดไฟล์ผิดปกติ
            </v-card-subtitle>
            <v-card-text>
              <v-row class="mb-2" align="center">
                <v-col cols="6" sm="3">
                  <v-select
                    v-model="suspiciousParams.days"
                    :items="[
                      { text: '7 วัน', value: 7 },
                      { text: '14 วัน', value: 14 },
                      { text: '30 วัน', value: 30 },
                      { text: '90 วัน', value: 90 }
                    ]"
                    item-text="text"
                    item-value="value"
                    label="ช่วงเวลา"
                    dense
                    filled
                    hide-details
                    @change="loadSuspiciousUsers"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-btn color="amber darken-3" dark small @click="loadSuspiciousUsers">
                    <v-icon left small>mdi-refresh</v-icon>วิเคราะห์ใหม่
                  </v-btn>
                </v-col>
              </v-row>

              <v-tabs v-model="suspiciousTab" color="amber darken-3">
                <v-tab>
                  พยายามเข้าถึงไม่มีสิทธิ์
                  <v-chip x-small class="ml-1" color="deep-orange" dark>{{ suspiciousUsers.unauthorized_access.length }}</v-chip>
                </v-tab>
                <v-tab>
                  Brute Force
                  <v-chip x-small class="ml-1" color="error" dark>{{ suspiciousUsers.brute_force.length }}</v-chip>
                </v-tab>
                <v-tab>
                  เข้าใช้งานนอกเวลา
                  <v-chip x-small class="ml-1" color="indigo" dark>{{ suspiciousUsers.off_hours_login.length }}</v-chip>
                </v-tab>
                <v-tab>
                  อัปโหลดไฟล์ผิดปกติ
                  <v-chip x-small class="ml-1" color="purple" dark>{{ suspiciousUsers.rejected_uploads.length }}</v-chip>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="suspiciousTab">
                <!-- Tab 1: Unauthorized access -->
                <v-tab-item>
                  <v-alert v-if="!suspiciousUsers.unauthorized_access.length" type="success" text class="mt-4">
                    ไม่พบผู้ใช้งานที่พยายามเข้าถึงหน้า/ฟังก์ชันที่ไม่มีสิทธิ์ในช่วงเวลาที่เลือก
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="unauthorizedHeaders"
                    :items="suspiciousUsers.unauthorized_access"
                    dense
                    class="mt-4"
                    :items-per-page="10"
                  >
                    <template v-slot:item.security_monitor_attempts="{ item }">
                      <v-chip v-if="item.security_monitor_attempts > 0" x-small color="red" dark>
                        พยายามเปิด Security Monitor {{ item.security_monitor_attempts }} ครั้ง
                      </v-chip>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:item.denied_count="{ item }">
                      <v-chip x-small :color="item.denied_count >= 5 ? 'error' : 'warning'" dark>{{ item.denied_count }}</v-chip>
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Tab 2: Brute force -->
                <v-tab-item>
                  <v-alert v-if="!suspiciousUsers.brute_force.length" type="success" text class="mt-4">
                    ไม่พบรูปแบบการเดารหัสผ่านผิดซ้ำๆ ในช่วงเวลาที่เลือก
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="bruteForceHeaders"
                    :items="suspiciousUsers.brute_force"
                    dense
                    class="mt-4"
                    :items-per-page="10"
                  >
                    <template v-slot:item.failed_count="{ item }">
                      <v-chip x-small :color="item.failed_count >= 10 ? 'error' : 'warning'" dark>{{ item.failed_count }}</v-chip>
                    </template>
                    <template v-slot:item.display_name="{ item }">
                      {{ item.display_name || "-" }}
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Tab 3: Off hours -->
                <v-tab-item>
                  <v-alert v-if="!suspiciousUsers.off_hours_login.length" type="success" text class="mt-4">
                    ไม่พบการเข้าใช้งานนอกเวลาทำการ (ก่อน 06:00 หรือหลัง 22:00) ในช่วงเวลาที่เลือก
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="offHoursHeaders"
                    :items="suspiciousUsers.off_hours_login"
                    dense
                    class="mt-4"
                    :items-per-page="10"
                  >
                    <template v-slot:item.display_name="{ item }">
                      {{ item.display_name || "-" }}
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Tab 4: Rejected uploads -->
                <v-tab-item>
                  <v-alert v-if="!suspiciousUsers.rejected_uploads.length" type="success" text class="mt-4">
                    ไม่พบความพยายามอัปโหลดไฟล์ที่นามสกุล/เนื้อหาไม่ตรงกันในช่วงเวลาที่เลือก
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="rejectedUploadHeaders"
                    :items="suspiciousUsers.rejected_uploads"
                    dense
                    class="mt-4"
                    :items-per-page="10"
                  >
                    <template v-slot:item.rejected_count="{ item }">
                      <v-chip x-small color="purple" dark>{{ item.rejected_count }}</v-chip>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>

          <!-- Active Sessions -->
          <h3 class="mb-2">Session ที่ Active อยู่ตอนนี้</h3>
          <v-data-table
            :headers="sessionHeaders"
            :items="activeSessions"
            :loading="loadingSessions"
            class="mb-8"
            dense
          >
            <template v-slot:item.actions="{ item }">
              <v-btn x-small color="error" @click="revokeSession(item)">
                Revoke
              </v-btn>
            </template>
          </v-data-table>

          <!-- Impersonation Log -->
          <h3 class="mb-2">ประวัติการสวมสิทธิ์ผู้ใช้งาน (Switch User)</h3>
          <v-data-table
            :headers="impersonationHeaders"
            :items="impersonationLog"
            :loading="loadingImpersonationLog"
            class="mb-8"
            dense
          >
            <template v-slot:item.ended_at="{ item }">
              <v-chip v-if="item.ended_at" x-small color="success" dark>{{ item.ended_at }}</v-chip>
              <v-chip v-else x-small color="warning" dark>ยังไม่กลับ</v-chip>
            </template>
          </v-data-table>

          <!-- Login Attempts -->
          <h3 class="mb-2">ประวัติการพยายาม Login</h3>
          <v-row class="mb-2">
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="loginFilter.identifier"
                label="ค้นหา identifier (id_card / user_name)"
                dense
                filled
                hide-details
                @keyup.enter="loadLoginAttempts"
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-select
                v-model="loginFilter.success"
                :items="[
                  { text: 'ทั้งหมด', value: '' },
                  { text: 'สำเร็จ', value: '1' },
                  { text: 'ล้มเหลว', value: '0' }
                ]"
                item-text="text"
                item-value="value"
                label="สถานะ"
                dense
                filled
                hide-details
                @change="loadLoginAttempts"
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-btn color="primary" @click="loadLoginAttempts">ค้นหา</v-btn>
            </v-col>
          </v-row>
          <v-data-table
            :headers="loginAttemptHeaders"
            :items="loginAttempts"
            :loading="loadingLoginAttempts"
            :server-items-length="loginAttemptsTotal"
            :options.sync="loginAttemptsOptions"
            class="mb-8"
            dense
          >
            <template v-slot:item.success="{ item }">
              <v-chip small :color="item.success ? 'success' : 'error'" dark>
                {{ item.success ? "สำเร็จ" : "ล้มเหลว" }}
              </v-chip>
            </template>
          </v-data-table>

          <!-- Access Denied -->
          <h3 class="mb-2">ประวัติการถูกปฏิเสธสิทธิ์ (401/403)</h3>
          <v-data-table
            :headers="accessDeniedHeaders"
            :items="accessDenied"
            :loading="loadingAccessDenied"
            :server-items-length="accessDeniedTotal"
            :options.sync="accessDeniedOptions"
            class="mb-8"
            dense
          />

          <!-- Sensitive Access Log -->
          <h3 class="mb-2">ประวัติการเข้าถึง/ส่งออกข้อมูลลับ</h3>
          <v-row class="mb-2">
            <v-col cols="12" sm="3">
              <v-select
                v-model="sensitiveAccessFilter.page_key"
                :items="sensitiveAccessPageOptions"
                item-text="text"
                item-value="value"
                label="หน้า"
                dense
                filled
                hide-details
                @change="loadSensitiveAccess"
              />
            </v-col>
            <v-col cols="12" sm="2">
              <v-select
                v-model="sensitiveAccessFilter.action"
                :items="[
                  { text: 'ทั้งหมด', value: '' },
                  { text: 'เข้าดู (view)', value: 'view' },
                  { text: 'ส่งออก (export)', value: 'export' }
                ]"
                item-text="text"
                item-value="value"
                label="การกระทำ"
                dense
                filled
                hide-details
                @change="loadSensitiveAccess"
              />
            </v-col>
            <v-col cols="12" sm="2">
              <v-text-field
                v-model="sensitiveAccessFilter.subject_id"
                label="ค้นหาผู้ใช้ (subject_id)"
                dense
                filled
                hide-details
                @keyup.enter="loadSensitiveAccess"
              />
            </v-col>
            <v-col cols="12" sm="2">
              <v-text-field
                v-model="sensitiveAccessFilter.date_from"
                label="ตั้งแต่วันที่"
                type="date"
                dense
                filled
                hide-details
                @change="loadSensitiveAccess"
              />
            </v-col>
            <v-col cols="12" sm="2">
              <v-text-field
                v-model="sensitiveAccessFilter.date_to"
                label="ถึงวันที่"
                type="date"
                dense
                filled
                hide-details
                @change="loadSensitiveAccess"
              />
            </v-col>
            <v-col cols="12" sm="1">
              <v-btn color="primary" @click="loadSensitiveAccess">ค้นหา</v-btn>
            </v-col>
          </v-row>
          <v-data-table
            :headers="sensitiveAccessHeaders"
            :items="sensitiveAccess"
            :loading="loadingSensitiveAccess"
            :server-items-length="sensitiveAccessTotal"
            :options.sync="sensitiveAccessOptions"
            dense
          >
            <template v-slot:item.page_key="{ item }">
              {{ sensitiveAccessPageLabel(item.page_key) }}
            </template>
            <template v-slot:item.action="{ item }">
              <v-chip small :color="item.action === 'export' ? 'warning' : 'info'" dark>
                {{ item.action === "export" ? "ส่งออก" : "เข้าดู" }}
              </v-chip>
            </template>
          </v-data-table>
        </base-material-card>
      </template>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "SecurityMonitor",
  data() {
    return {
      forbidden: false,
      summary: {
        locked_accounts_now: 0,
        failed_logins_24h: 0,
        access_denied_24h: 0,
        active_sessions_now: 0,
        sensitive_export_24h: 0,
        password_change_pending: {
          user: 0,
          personnel_temporary: 0,
          personnel_etc: 0,
          total: 0
        }
      },
      lockedAccounts: [],
      loadingLockedAccounts: false,
      lockedAccountHeaders: [
        { text: "ชื่อ (ถ้าพบ)", value: "display_name" },
        { text: "Identifier", value: "identifier" },
        { text: "ประเภท Login", value: "login_type" },
        { text: "จำนวนครั้งที่ผิด", value: "failed_count" },
        { text: "ปลดล็อกเองใน", value: "retry_after" },
        { text: "ล่าสุด", value: "last_attempt" },
        { text: "", value: "actions", sortable: false }
      ],
      activeSessions: [],
      loadingSessions: false,
      sessionHeaders: [
        { text: "ชื่อ", value: "display_name" },
        { text: "ประเภท", value: "subject_type" },
        { text: "Role", value: "role" },
        { text: "Security Role", value: "security_role" },
        { text: "Login เมื่อ", value: "created_at" },
        { text: "หมดอายุ", value: "expires_at" },
        { text: "Token", value: "session_token_display" },
        { text: "", value: "actions", sortable: false }
      ],
      impersonationLog: [],
      loadingImpersonationLog: false,
      impersonationHeaders: [
        { text: "เริ่ม", value: "started_at" },
        { text: "ผู้ดำเนินการ (แอดมิน)", value: "admin_display_name" },
        { text: "บัญชีเป้าหมาย", value: "target_display_name" },
        { text: "ประเภท", value: "target_subject_type" },
        { text: "Role เป้าหมาย", value: "target_role" },
        { text: "สิ้นสุด", value: "ended_at" },
        { text: "IP", value: "ip_address" }
      ],
      loginAttempts: [],
      loginAttemptsTotal: 0,
      loadingLoginAttempts: false,
      loginAttemptsOptions: { page: 1, itemsPerPage: 20 },
      loginFilter: { identifier: "", success: "" },
      loginAttemptHeaders: [
        { text: "Identifier", value: "identifier" },
        { text: "ประเภท Login", value: "login_type" },
        { text: "ผลลัพธ์", value: "success" },
        { text: "IP", value: "ip_address" },
        { text: "เวลา", value: "created_at" }
      ],
      accessDenied: [],
      accessDeniedTotal: 0,
      loadingAccessDenied: false,
      accessDeniedOptions: { page: 1, itemsPerPage: 20 },
      accessDeniedHeaders: [
        { text: "Endpoint", value: "endpoint" },
        { text: "เหตุผล", value: "reason" },
        { text: "Token (บางส่วน)", value: "session_token_partial" },
        { text: "IP", value: "ip_address" },
        { text: "เวลา", value: "created_at" }
      ],
      sensitiveAccess: [],
      sensitiveAccessTotal: 0,
      loadingSensitiveAccess: false,
      sensitiveAccessOptions: { page: 1, itemsPerPage: 20 },
      sensitiveAccessFilter: { page_key: "", action: "", subject_id: "", date_from: "", date_to: "" },
      sensitiveAccessPageOptions: [
        { text: "ทั้งหมด", value: "" },
        { text: "สรุปผลย้าย ผอ./รอง ผอ.", value: "transference_manage_report" },
        { text: "สรุปผลย้ายสายครู", value: "conditons_transfer_success_reportmv" },
        { text: "กรอบอัตรากำลัง/ตำแหน่งว่าง (jor18)", value: "jor18" },
        { text: "ประมวลผลย้าย/จับคู่ตำแหน่งว่าง", value: "process_transfer" },
        { text: "รายงานสาขาวิชาสำหรับตำแหน่งว่าง", value: "jor18_report" },
        { text: "รายงานตัวครูผู้ช่วย ณ สถานศึกษา (โอนย้าย)", value: "manage_assistant_college_report_tran" },
        { text: "รายงานตัว/อนุมัติครูผู้ช่วย", value: "manage_assistant_teacher_data" }
      ],
      sensitiveAccessHeaders: [
        { text: "เวลา", value: "created_at" },
        { text: "ชื่อผู้ใช้", value: "display_name" },
        { text: "Role", value: "role" },
        { text: "หน้า", value: "page_key" },
        { text: "การกระทำ", value: "action" },
        { text: "รายละเอียด", value: "context" },
        { text: "IP", value: "ip_address" }
      ],
      suspiciousTab: 0,
      suspiciousParams: { days: 14 },
      suspiciousUsers: {
        unauthorized_access: [],
        brute_force: [],
        off_hours_login: [],
        rejected_uploads: []
      },
      unauthorizedHeaders: [
        { text: "ประเภท", value: "subject_type" },
        { text: "ผู้ใช้/IP", value: "subject_id" },
        { text: "จำนวนครั้งที่ถูกปฏิเสธ", value: "denied_count" },
        { text: "พยายามเปิด Security Monitor", value: "security_monitor_attempts" },
        { text: "Endpoint ที่พยายามเรียก", value: "endpoints" },
        { text: "IP", value: "ip_addresses" },
        { text: "ล่าสุด", value: "last_seen" }
      ],
      bruteForceHeaders: [
        { text: "Identifier", value: "identifier" },
        { text: "ชื่อ (ถ้าพบ)", value: "display_name" },
        { text: "ประเภท Login", value: "login_type" },
        { text: "จำนวนครั้งที่ผิด", value: "failed_count" },
        { text: "จำนวน IP ที่ใช้", value: "distinct_ips" },
        { text: "IP", value: "ip_addresses" },
        { text: "ล่าสุด", value: "last_seen" }
      ],
      offHoursHeaders: [
        { text: "Identifier", value: "identifier" },
        { text: "ชื่อ (ถ้าพบ)", value: "display_name" },
        { text: "ประเภท Login", value: "login_type" },
        { text: "จำนวนครั้งนอกเวลา", value: "off_hours_count" },
        { text: "IP", value: "ip_addresses" },
        { text: "ล่าสุด", value: "last_seen" }
      ],
      rejectedUploadHeaders: [
        { text: "ประเภท", value: "subject_type" },
        { text: "ผู้ใช้/IP", value: "subject_key" },
        { text: "จำนวนครั้งที่ถูกปฏิเสธ", value: "rejected_count" },
        { text: "Endpoint", value: "endpoints" },
        { text: "รายละเอียด", value: "details" },
        { text: "IP", value: "ip_addresses" },
        { text: "ล่าสุด", value: "last_seen" }
      ]
    };
  },
  watch: {
    "loginAttemptsOptions.page": "loadLoginAttempts",
    "accessDeniedOptions.page": "loadAccessDenied",
    "sensitiveAccessOptions.page": "loadSensitiveAccess"
  },
  async mounted() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      await Promise.all([
        this.loadSummary(),
        this.loadLockedAccounts(),
        this.loadActiveSessions(),
        this.loadImpersonationLog(),
        this.loadLoginAttempts(),
        this.loadAccessDenied(),
        this.loadSuspiciousUsers(),
        this.loadSensitiveAccess()
      ]);
    },
    // axios reject (throw) เมื่อ HTTP status ไม่ใช่ 2xx เช่น 401/403 ที่ requireSuperAdmin() ส่งกลับ
    // ต้อง try/catch ทุกจุดที่เรียก endpoint นี้ ไม่งั้น error จะหลุดไปที่ Vue global errorHandler
    // แทนที่จะโชว์ข้อความ "ไม่มีสิทธิ์" ที่ตั้งใจไว้ในหน้านี้
    async callApi(endpoint, payload = {}) {
      try {
        const result = await this.$http.post(endpoint, payload);
        return result.data;
      } catch (error) {
        const status = error.response ? error.response.status : null;
        if (status === 401 || status === 403) {
          this.forbidden = true;
          return null;
        }
        throw error;
      }
    },
    async loadSummary() {
      const data = await this.callApi("security_monitor.summary.php");
      if (!data) return;
      this.summary = data.data;
    },
    async loadLockedAccounts() {
      this.loadingLockedAccounts = true;
      const data = await this.callApi("security_monitor.locked_accounts.php");
      this.loadingLockedAccounts = false;
      if (!data) return;
      this.lockedAccounts = data.data;
    },
    async unlockAccount(item) {
      if (!confirm(`ยืนยันยกเลิกการล็อกบัญชี "${item.display_name || item.identifier}"?`)) return;
      const data = await this.callApi("security_monitor.unlock_account.php", {
        identifier: item.identifier,
        login_type: item.login_type
      });
      if (!data) return;
      alert(data.message);
      await this.loadLockedAccounts();
      await this.loadSummary();
    },
    async loadActiveSessions() {
      this.loadingSessions = true;
      const data = await this.callApi("security_monitor.active_sessions.php");
      this.loadingSessions = false;
      if (!data) return;
      this.activeSessions = data.data;
    },
    async loadImpersonationLog() {
      this.loadingImpersonationLog = true;
      const data = await this.callApi("security_monitor.impersonation_log.php");
      this.loadingImpersonationLog = false;
      if (!data) return;
      this.impersonationLog = data.data;
    },
    async loadLoginAttempts() {
      this.loadingLoginAttempts = true;
      const data = await this.callApi("security_monitor.login_attempts.php", {
        identifier: this.loginFilter.identifier,
        success: this.loginFilter.success,
        page: this.loginAttemptsOptions.page,
        per_page: this.loginAttemptsOptions.itemsPerPage
      });
      this.loadingLoginAttempts = false;
      if (!data) return;
      this.loginAttempts = data.data;
      this.loginAttemptsTotal = data.total;
    },
    async loadAccessDenied() {
      this.loadingAccessDenied = true;
      const data = await this.callApi("security_monitor.access_denied.php", {
        page: this.accessDeniedOptions.page,
        per_page: this.accessDeniedOptions.itemsPerPage
      });
      this.loadingAccessDenied = false;
      if (!data) return;
      this.accessDenied = data.data;
      this.accessDeniedTotal = data.total;
    },
    async loadSuspiciousUsers() {
      const data = await this.callApi("security_monitor.suspicious_users.php", {
        days: this.suspiciousParams.days
      });
      if (!data) return;
      this.suspiciousUsers = data.data;
    },
    async loadSensitiveAccess() {
      this.loadingSensitiveAccess = true;
      const data = await this.callApi("security_monitor.sensitive_access.php", {
        page_key: this.sensitiveAccessFilter.page_key,
        action: this.sensitiveAccessFilter.action,
        subject_id: this.sensitiveAccessFilter.subject_id,
        date_from: this.sensitiveAccessFilter.date_from,
        date_to: this.sensitiveAccessFilter.date_to,
        page: this.sensitiveAccessOptions.page,
        per_page: this.sensitiveAccessOptions.itemsPerPage
      });
      this.loadingSensitiveAccess = false;
      if (!data) return;
      this.sensitiveAccess = data.data;
      this.sensitiveAccessTotal = data.total;
    },
    sensitiveAccessPageLabel(pageKey) {
      const found = this.sensitiveAccessPageOptions.find(o => o.value === pageKey);
      return found ? found.text : pageKey;
    },
    async revokeSession(item) {
      if (!confirm(`ยืนยัน revoke session ของ "${item.display_name}"?`)) return;
      const data = await this.callApi("security_monitor.revoke_session.php", {
        session_token: item.session_token
      });
      if (!data) return;
      alert(data.message);
      await this.loadActiveSessions();
    }
  }
};
</script>
